import React from "react";
import { useParams } from "react-router-dom";

import ChatLayout from "@layouts/ChatLayout";
import { Protected } from "@/components/auth/Protected";
import { featureFlags } from "@/config/featureFlags";
import { isVanityAliasSegment } from "@/lib/meetVanity";
import { MeetProtected } from "./standaloneMeetRoutes";
import VanityMeetJoin from "./VanityMeetJoin";

/**
 * Owns the SHARED root segment `/:chatId?`. A chatId (uuid) and a vanity alias
 * are the SAME react-router pattern, so ONE component must decide between them —
 * by SHAPE, with zero network cost (see `meetVanity.ts`):
 *
 *   - alias-shaped segment + vanity enabled → the vanity meet join, rendered
 *     under `MeetProtected` (carries the login return-path) and OUTSIDE
 *     `ChatLayout`. Crucially it does NOT navigate, so `/<alias>` stays in the
 *     address bar, and because `ChatLayout` never mounts, `useChatRouteSync`'s
 *     not-found bounce can never fire for an alias.
 *   - everything else → the unchanged chat experience: `Protected` + a real
 *     `ChatLayout` whose `<Outlet/>` renders the `index` `ChatPage` child (wired
 *     in `App.tsx`). This keeps `ChatPage` an Outlet-rendered nested route
 *     (router-equivalent to the previous layout-route form), so nothing about
 *     the chat branch changes.
 *
 * Vanity is subordinate to the master `orionMeetUi` + `standaloneMeet` flags
 * (mirrors `standaloneMeetRoutes.tsx`). When the flag is OFF the guard ALWAYS
 * takes the chat branch → byte-identical to today.
 */
const RootRouteGuard: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>();
  const vanityEnabled =
    featureFlags.orionMeetUi &&
    featureFlags.standaloneMeet &&
    featureFlags.meetVanity;

  if (chatId && vanityEnabled && isVanityAliasSegment(chatId)) {
    return (
      <MeetProtected>
        <VanityMeetJoin />
      </MeetProtected>
    );
  }

  return (
    <Protected>
      <ChatLayout />
    </Protected>
  );
};

export default RootRouteGuard;
